'use client';

import { useState, useEffect, useMemo, memo } from 'react';
import Image from 'next/image';
import { createPortal } from 'react-dom';
import DOMPurify from 'isomorphic-dompurify';
import parse from 'html-react-parser';
import { PopupComponentProps } from '@/components/popup/PopupHandler';

function SavingsGuidePopup({ 
  closePopup, 
  popupId,
  popupData,
  translations
}: PopupComponentProps) {
  const [mounted, setMounted] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [gdprConsent, setGdprConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [honeypot, setHoneypot] = useState('');

  // Use fetched data from WordPress
  const title = popupData?.title;
  const description = popupData?.description;
  const imageUrl = popupData?.imageUrl;
  const imageAlt = popupData?.imageAlt;
  const ctaText = popupData?.ctaText;
  const ctaRoute = popupData?.ctaRoute;
  const disclaimer = popupData?.disclaimer;
  const backgroundColor = popupData?.backgroundColor;

  // Memoize HTML sanitization and parsing for performance
  const sanitizedDescription = useMemo(() => {
    if (!description) return null;
    
    const cleanHtml = DOMPurify.sanitize(description, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'i', 'b', 'ul', 'ol', 'li', 'a', 'span'],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
      KEEP_CONTENT: true,
      ALLOW_DATA_ATTR: false,
      ALLOW_UNKNOWN_PROTOCOLS: false,
    });
    
    return parse(cleanHtml);
  }, [description]);

  const sanitizedDisclaimer = useMemo(() => {
    if (!disclaimer) return null;
    
    const cleanHtml = DOMPurify.sanitize(disclaimer, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'i', 'b', 'ul', 'ol', 'li', 'a', 'span'],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
      KEEP_CONTENT: true,
      ALLOW_DATA_ATTR: false,
      ALLOW_UNKNOWN_PROTOCOLS: false,
    });
    
    return parse(cleanHtml);
  }, [disclaimer]);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Don't show popup if required information is missing
  if (!title || !ctaText || !ctaRoute) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!gdprConsent) {
      setError(translations.errors.gdprConsentRequired);
      return;
    }
    
    if (!ctaRoute) {
      setError(translations.errors.apiRouteNotConfigured);
      return;
    }
    
    setError('');
    setIsSubmitting(true);

    try {
      // Force use of current page's origin and protocol to avoid SSL errors
      // Remove trailing slash if present to avoid routing issues
      const cleanRoute = ctaRoute.endsWith('/') ? ctaRoute.slice(0, -1) : ctaRoute;
      
      let apiUrl: string;
      if (typeof window !== 'undefined') {
        // Force HTTP in development (localhost)
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const origin = isLocalhost 
          ? `http://${window.location.host}`
          : window.location.origin;
        
        apiUrl = `${origin}${cleanRoute.startsWith('/') ? cleanRoute : '/' + cleanRoute}`;
      } else {
        apiUrl = cleanRoute;
      }

      let response;
      try {
        // Create a new URL object to ensure it's properly formatted
        const fetchUrl = new URL(apiUrl);
        
        // Force HTTP for localhost (CSP upgrade-insecure-requests will try to upgrade, but we need HTTP)
        if (fetchUrl.hostname === 'localhost' || fetchUrl.hostname === '127.0.0.1') {
          fetchUrl.protocol = 'http:';
          // Remove trailing slash to avoid redirect issues
          if (fetchUrl.pathname.endsWith('/') && fetchUrl.pathname !== '/') {
            fetchUrl.pathname = fetchUrl.pathname.slice(0, -1);
          }
        }
        
        const finalUrl = fetchUrl.toString();
        
        // Use Request object to have more control
        const request = new Request(finalUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            firstName,
            email,
            popupId,
            gdprConsent: true,
            website: honeypot, // Honeypot field - should be empty
          }),
        });
        
        response = await fetch(request);
      } catch {
        // Network error - mask technical details
        throw new Error('network_error');
      }

      if (!response.ok) {
        let errorMessage = translations.errors.somethingWentWrong;
        try {
          const errorData = await response.json();
          // Only use server error message if it's a safe validation error
          if (errorData.error && typeof errorData.error === 'string') {
            // Check if it's a validation error (safe to show)
            if (response.status === 400 && (
              errorData.error.includes('required') || 
              errorData.error.includes('email') ||
              errorData.error.includes('format')
            )) {
              errorMessage = errorData.error;
            }
          }
        } catch {
          // If response is not JSON, use generic error
        }
        throw new Error(errorMessage);
      }

      await response.json();

      setSuccess(true);
      setTimeout(() => {
        closePopup();
      }, 2000);
    } catch (error) {
      if (error instanceof Error) {
        // Map specific error types to user-friendly messages
        if (error.message === 'network_error') {
          setError(translations.errors.networkError || translations.errors.somethingWentWrong);
        } else if (error.message === translations.errors.gdprConsentRequired || 
                   error.message === translations.errors.apiRouteNotConfigured) {
          // These are already user-friendly
          setError(error.message);
        } else {
          // For other errors, use the error message if it's safe, otherwise use generic
          setError(error.message || translations.errors.somethingWentWrong);
        }
      } else {
        setError(translations.errors.somethingWentWrong);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      closePopup();
    }
  };

  const content = success ? (
    <div className="text-center py-8">
      <div className="text-green-600 text-4xl mb-4">✓</div>
      <h3 className="text-xl font-semibold text-gray-900 mb-2">{translations.success.title}</h3>
      <p className="text-gray-600">{translations.success.message}</p>
    </div>
  ) : (
    <div className="flex flex-col md:flex-row">
      {imageUrl && (
        <div 
          className="w-full md:w-1/2 relative aspect-square md:aspect-square overflow-hidden md:rounded-l-lg"
          style={{ backgroundColor: backgroundColor || '#f3f4f6' }}
        >
          <Image
            src={imageUrl}
            alt={imageAlt || ''}
            fill
            className="object-contain"
            sizes="(max-width: 768px) 100vw, 50vw"
          />
        </div>
      )}

      <div className={`w-full ${imageUrl ? 'md:w-1/2' : 'md:w-full'} px-6 py-6 md:py-8 flex flex-col justify-center`}>
        <div className="space-y-8">
          <div className="space-y-2">
            {title && (
              <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            )}
            {sanitizedDescription && (
              <div className="text-gray-600 prose prose-sm max-w-none">
                {sanitizedDescription}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="relative">
              <label 
                htmlFor="firstName" 
                className="absolute -top-2.5 left-3 px-2 bg-white text-xs font-medium text-gray-700 z-10"
              >
                {translations.form.firstNameLabel}
              </label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                placeholder={translations.form.firstNamePlaceholder}
              />
            </div>

            <div className="relative">
              <label 
                htmlFor="email" 
                className="absolute -top-2.5 left-3 px-2 bg-white text-xs font-medium text-gray-700 z-10"
              >
                {translations.form.emailLabel}
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                placeholder={translations.form.emailPlaceholder}
              />
            </div>

            {error && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">{error}</div>
            )}

            {/* Honeypot field - hidden from users but visible to bots */}
            <div 
              className="sr-only" 
              aria-hidden="true"
              style={{ 
                position: 'absolute',
                left: '-9999px',
                width: '1px',
                height: '1px',
                overflow: 'hidden'
              }}
            >
              <label htmlFor="website">
                Please leave this field empty
              </label>
              <input
                type="text"
                id="website"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-label="Do not fill this field"
              />
            </div>

            <div className="flex items-center justify-start">
              <input
                type="checkbox"
                id="gdpr-consent"
                checked={gdprConsent}
                onChange={(e) => setGdprConsent(e.target.checked)}
                required
                className="mt-1 h-5 w-5 cursor-pointer text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="gdpr-consent" className="ml-2 text-sm text-gray-700">
                {sanitizedDisclaimer}
              </label>
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !gdprConsent}
              className="w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              {isSubmitting ? translations.form.processing : ctaText}
            </button>
          </form>
        </div>
      </div>
    </div>
  );

  if (!mounted) return null;

  return (
    <>
      {createPortal(
        <div
          className="fixed inset-0 bg-black/50 z-[999999]"
          onClick={handleBackdropClick}
        >
          <div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-[1000px] bg-white rounded-lg shadow-lg z-[1000000] max-h-[90vh] overflow-y-auto p-0"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              {content}
              <button
                onClick={closePopup}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl leading-none cursor-pointer transition-colors bg-white rounded-full w-8 h-8 flex items-center justify-center shadow-md z-10"
                aria-label={translations.form.closePopup}
              >
                ×
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default memo(SavingsGuidePopup);

